import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, Search, CheckCircle, Clock, XCircle, Eye, Download,
  Loader2, Mail, ArrowLeft, DollarSign, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast, Toaster } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  sent: { color: 'bg-blue-500', icon: FileText, label: 'Sent' },
  viewed: { color: 'bg-purple-500', icon: Eye, label: 'Awaiting Payment' },
  partial: { color: 'bg-amber-500', icon: Clock, label: 'Partially Paid' },
  paid: { color: 'bg-emerald-500', icon: CheckCircle, label: 'Paid' },
  expired: { color: 'bg-red-500', icon: XCircle, label: 'Expired' }
};

const CustomerInvoicePortal = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const emailParam = searchParams.get('email');
  
  const [email, setEmail] = useState(emailParam || '');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  
  // Fetch invoices for email
  const fetchInvoices = async (searchEmail) => {
    if (!searchEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    setSearched(true);
    try {
      const response = await axios.get(`${API}/customer-invoices?email=${encodeURIComponent(searchEmail)}`);
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };
  
  // Auto-fetch if email in URL
  useEffect(() => {
    if (emailParam) {
      fetchInvoices(emailParam);
    }
  }, [emailParam]);
  
  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchInvoices(email);
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Download PDF
  const downloadPdf = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="bg-slate-800/80 backdrop-blur border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <h1 className="text-3xl font-bold text-white">Biggest Little CPR</h1>
          <p className="text-slate-400 mt-1">My Invoices</p>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Search Form */}
        <Card className="bg-slate-800/50 border-slate-700 p-6 mb-8">
          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <Label className="text-slate-400">Email Address</Label>
              <div className="flex gap-3 mt-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="pl-11 bg-slate-700 border-slate-600 text-white h-12"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-teal-600 hover:bg-teal-500 h-12 px-6"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Search className="h-5 w-5 mr-2" />
                      Find Invoices
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </Card>
        
        {/* Results */}
        {searched && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-12 w-12 animate-spin text-teal-400" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Invoices Found</h3>
                <p className="text-slate-400">
                  No invoices found for <span className="text-teal-400">{email}</span>
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-white mb-4">
                  {invoices.length} Invoice{invoices.length !== 1 ? 's' : ''} Found
                </h2>
                
                {invoices.map(invoice => {
                  const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.sent;
                  const StatusIcon = statusConfig.icon;
                  const isPaid = invoice.status === 'paid';
                  const isExpired = invoice.status === 'expired';
                  
                  return (
                    <Card 
                      key={invoice.invoice_id} 
                      className="bg-slate-800/50 border-slate-700 p-6 hover:border-teal-600/50 transition-colors"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                          {/* Status Icon */}
                          <div className={`w-12 h-12 rounded-xl ${statusConfig.color} flex items-center justify-center flex-shrink-0`}>
                            <StatusIcon className="h-6 w-6 text-white" />
                          </div>
                          
                          {/* Invoice Info */}
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-lg text-white font-medium">
                                {invoice.invoice_id}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs text-white ${statusConfig.color}`}>
                                {statusConfig.label}
                              </span>
                              {invoice.invoice_type === 'quote' && (
                                <span className="px-2 py-0.5 rounded text-xs bg-purple-600 text-white">
                                  Quote
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-400 mt-1">
                              Created: {formatDate(invoice.created_at)}
                              {invoice.due_date && (
                                <span className="ml-3">Due: {formatDate(invoice.due_date)}</span>
                              )}
                            </p>
                            {invoice.items && (
                              <p className="text-sm text-slate-500 mt-1">
                                {invoice.items.length} item{invoice.items.length !== 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Amount & Actions */}
                        <div className="flex flex-col md:items-end gap-3">
                          <div className="text-right">
                            <p className="text-2xl font-bold text-white">${invoice.total?.toFixed(2)}</p>
                            {invoice.amount_paid > 0 && !isPaid && (
                              <p className="text-sm text-amber-400">
                                Due: ${invoice.amount_due?.toFixed(2)}
                              </p>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadPdf(invoice.invoice_id)}
                              className="border-slate-600 text-white hover:bg-slate-700"
                            >
                              <Download className="h-4 w-4 mr-1" />
                              PDF
                            </Button>
                            
                            {!isPaid && !isExpired && invoice.invoice_type === 'invoice' && (
                              <Button
                                size="sm"
                                onClick={() => navigate(`/invoice/${invoice.invoice_id}`)}
                                className="bg-teal-600 hover:bg-teal-500"
                              >
                                <DollarSign className="h-4 w-4 mr-1" />
                                Pay Now
                              </Button>
                            )}
                            
                            {(isPaid || isExpired || invoice.invoice_type === 'quote') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/invoice/${invoice.invoice_id}`)}
                                className="border-slate-600 text-white hover:bg-slate-700"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
        
        {/* Help Text */}
        {!searched && (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">View Your Invoices</h3>
            <p className="text-slate-400 max-w-md mx-auto">
              Enter the email address associated with your invoices to view and pay them.
            </p>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="bg-slate-800/50 border-t border-slate-700 mt-12">
        <div className="max-w-4xl mx-auto px-6 py-6 text-center">
          <p className="text-slate-500 text-sm">
            Biggest Little CPR • Professional CPR Training<br />
            Questions? Contact us at info@biggestlittlecpr.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerInvoicePortal;
